import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronRight, Upload, X, FileText, Image } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
interface UploadedFile {
  file: File;
  preview?: string;
}
type MembershipType = 'individu' | 'organisasi' | 'badan_usaha' | 'mitra';
export function RegisterStep3() {
  const navigate = useNavigate();
  const [membershipType, setMembershipType] = useState<MembershipType | null>(null);
  const [files, setFiles] = useState({
    profilePhoto: null as UploadedFile | null,
    identityDocument: null as UploadedFile | null,
    educationCertificate: null as UploadedFile | null,
    workExperience: null as UploadedFile | null,
    // For Badan Usaha
    nibOrAkta: null as UploadedFile | null,
    npwpDocument: null as UploadedFile | null,
    // For Organisasi
    aktaOrSK: null as UploadedFile | null,
    organizationProfile: null as UploadedFile | null,
    // For Mitra
    moaDocument: null as UploadedFile | null,
    partnershipProposal: null as UploadedFile | null
  });
  useEffect(() => {
    const savedData = localStorage.getItem('registrationData');
    if (!savedData) {
      navigate('/register/step1');
      return;
    }
    const data = JSON.parse(savedData);
    setMembershipType(data.membershipType);
  }, [navigate]);
  const handleFileSelect = (type: keyof typeof files, file: File, maxSize: number, acceptedTypes: string[]) => {
    // Validate size
    if (file.size > maxSize) {
      alert(`Ukuran file maksimal ${maxSize / (1024 * 1024)}MB`);
      return;
    }
    // Validate type
    if (!acceptedTypes.includes(file.type)) {
      alert('Format file tidak didukung');
      return;
    }
    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFiles(prev => ({
          ...prev,
          [type]: {
            file,
            preview: reader.result as string
          }
        }));
      };
      reader.readAsDataURL(file);
    } else {
      setFiles(prev => ({
        ...prev,
        [type]: {
          file
        }
      }));
    }
  };
  const handleRemoveFile = (type: keyof typeof files) => {
    setFiles(prev => ({
      ...prev,
      [type]: null
    }));
  };
  const handleSubmit = () => {
    // Common validation
    if (!files.profilePhoto) {
      alert('Foto profil wajib diunggah');
      return;
    }
    if (!files.identityDocument) {
      alert('Kartu identitas wajib diunggah');
      return;
    }
    // Validation based on membership type
    if (membershipType === 'badan_usaha') {
      if (!files.nibOrAkta) {
        alert('NIB atau Akta Pendirian wajib diunggah untuk Badan Usaha');
        return;
      }
      if (!files.npwpDocument) {
        alert('Dokumen NPWP wajib diunggah untuk Badan Usaha');
        return;
      }
    } else if (membershipType === 'organisasi') {
      if (!files.aktaOrSK) {
        alert('Akta Pendirian atau SK Kemenkumham wajib diunggah untuk Organisasi');
        return;
      }
    } else if (membershipType === 'mitra') {
      if (!files.moaDocument) {
        alert('Dokumen MoU/Proposal Kemitraan wajib diunggah untuk Mitra');
        return;
      }
    }
    // Save to localStorage
    const savedData = JSON.parse(localStorage.getItem('registrationData') || '{}');
    const documentsData: any = {
      profilePhoto: files.profilePhoto?.file.name,
      identityDocument: files.identityDocument?.file.name
    };
    // Add type-specific documents
    if (membershipType === 'individu') {
      documentsData.educationCertificate = files.educationCertificate?.file.name;
      documentsData.workExperience = files.workExperience?.file.name;
    } else if (membershipType === 'badan_usaha') {
      documentsData.nibOrAkta = files.nibOrAkta?.file.name;
      documentsData.npwpDocument = files.npwpDocument?.file.name;
    } else if (membershipType === 'organisasi') {
      documentsData.aktaOrSK = files.aktaOrSK?.file.name;
      documentsData.organizationProfile = files.organizationProfile?.file.name;
    } else if (membershipType === 'mitra') {
      documentsData.moaDocument = files.moaDocument?.file.name;
      documentsData.partnershipProposal = files.partnershipProposal?.file.name;
    }
    localStorage.setItem('registrationData', JSON.stringify({
      ...savedData,
      documents: documentsData
    }));
    navigate('/register/step4');
  };
  const DropZone = ({
    label,
    type,
    maxSize,
    acceptedFormats,
    required,
    helperText
  }: {
    label: string;
    type: keyof typeof files;
    maxSize: number;
    acceptedFormats: string[];
    required?: boolean;
    helperText?: string;
  }) => {
    const uploadedFile = files[type];
    const isImage = type === 'profilePhoto';
    return <div data-id="element-4902">
        <label className="text-xs sm:text-sm font-medium text-neutral-900 block mb-3" data-id="element-4903">
          {label} {required && <span className="text-error" data-id="element-4904">*</span>}
        </label>
        {helperText && <p className="text-xs text-neutral-500 mb-2" data-id="element-4905">{helperText}</p>}

        {!uploadedFile ? <label className={`flex flex-col items-center justify-center ${isImage ? 'w-48 h-48' : 'w-full h-32'} border-2 border-dashed border-lime-500 rounded-lg cursor-pointer hover:border-lime-600 transition-colors bg-lime-50`} data-id="element-4906">
            <Upload className="w-6 h-6 sm:w-8 sm:h-8 text-lime-600 mb-2" data-id="element-4907" />
            <span className="text-xs sm:text-sm text-neutral-600 mb-1" data-id="element-4908">
              Klik untuk upload
            </span>
            <span className="text-xs text-neutral-500 text-center px-2" data-id="element-4909">
              {acceptedFormats.map(f => f.split('/')[1].toUpperCase()).join(', ')}{' '}
              (Max {maxSize / (1024 * 1024)}MB)
            </span>
            <input type="file" className="hidden" accept={acceptedFormats.join(',')} onChange={e => {
          const file = e.target.files?.[0];
          if (file) {
            handleFileSelect(type, file, maxSize, acceptedFormats);
          }
        }} data-id="element-4910" />
          </label> : <div className={`relative ${isImage ? 'w-48 h-48' : 'w-full'} border-2 border-lime-500 rounded-lg overflow-hidden`} data-id="element-4911">
            {uploadedFile.preview ? <img src={uploadedFile.preview} alt="Preview" className="w-full h-full object-cover" data-id="element-4912" /> : <div className="flex items-center gap-3 p-3 sm:p-4" data-id="element-4913">
                <FileText className="w-6 h-6 sm:w-8 sm:h-8 text-lime-600 flex-shrink-0" data-id="element-4914" />
                <div className="flex-1 min-w-0" data-id="element-4915">
                  <p className="text-xs sm:text-sm font-medium text-neutral-900 truncate" data-id="element-4916">
                    {uploadedFile.file.name}
                  </p>
                  <p className="text-xs text-neutral-500" data-id="element-4917">
                    {(uploadedFile.file.size / 1024).toFixed(0)} KB
                  </p>
                </div>
              </div>}
            <button type="button" onClick={() => handleRemoveFile(type)} className="absolute top-2 right-2 p-1 bg-error text-white rounded-full hover:bg-error/90" data-id="element-4918">
              <X className="w-4 h-4" data-id="element-4919" />
            </button>
          </div>}
      </div>;
  };
  if (!membershipType) return null;
  return <div className="min-h-screen bg-gradient-to-br from-atios-primary to-blue-700" data-id="element-4920">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12" data-id="element-4921">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8" data-id="element-4922">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2 sm:mb-3" data-id="element-4923">
            Upload Dokumen Pendukung
          </h1>
          <p className="text-sm sm:text-base text-white/80" data-id="element-4924">
            {membershipType === 'individu' && 'Upload dokumen pribadi Anda'}
            {membershipType === 'organisasi' && 'Upload dokumen organisasi'}
            {membershipType === 'badan_usaha' && 'Upload dokumen perusahaan'}
            {membershipType === 'mitra' && 'Upload dokumen kemitraan'}
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8 sm:mb-12" data-id="element-4925">
          <div className="flex items-center justify-between mb-2" data-id="element-4926">
            <span className="text-xs sm:text-sm font-medium text-white" data-id="element-4927">
              Langkah 3 dari 5
            </span>
            <span className="text-xs sm:text-sm text-white/80" data-id="element-4928">60%</span>
          </div>
          <div className="w-full bg-white/20 rounded-full h-2" data-id="element-4929">
            <div className="bg-cyan-400 h-2 rounded-full transition-all" style={{
            width: '60%'
          }} data-id="element-4930" />
          </div>
        </div>

        {/* Upload Sections */}
        <div className="space-y-4 sm:space-y-6" data-id="element-4931">
          {/* Common Documents for All Types */}
          <Card data-id="element-4932">
            <CardHeader data-id="element-4933">
              <CardTitle className="text-base sm:text-lg" data-id="element-4934">
                Dokumen Umum
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 sm:space-y-6" data-id="element-4935">
              <DropZone label="Foto Profil" type="profilePhoto" maxSize={5 * 1024 * 1024} acceptedFormats={['image/jpeg', 'image/png']} required helperText="Foto formal dengan latar belakang polos" data-id="element-4936" />
              <DropZone label="Kartu Identitas (KTP/Paspor)" type="identityDocument" maxSize={10 * 1024 * 1024} acceptedFormats={['application/pdf', 'image/jpeg', 'image/png']} required helperText="KTP atau Paspor yang masih berlaku" data-id="element-4937" />
            </CardContent>
          </Card>

          {/* ANGGOTA INDIVIDU - Optional Documents */}
          {membershipType === 'individu' && <Card data-id="element-4938">
              <CardHeader data-id="element-4939">
                <CardTitle className="text-base sm:text-lg" data-id="element-4940">
                  Dokumen Tambahan (Opsional)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 sm:space-y-6" data-id="element-4941">
                <DropZone label="Sertifikat Pendidikan" type="educationCertificate" maxSize={10 * 1024 * 1024} acceptedFormats={['application/pdf', 'image/jpeg', 'image/png']} helperText="Ijazah atau sertifikat pendidikan terakhir" data-id="element-4942" />
                <DropZone label="Surat Keterangan Kerja" type="workExperience" maxSize={10 * 1024 * 1024} acceptedFormats={['application/pdf', 'image/jpeg', 'image/png']} helperText="Surat keterangan dari perusahaan tempat bekerja" data-id="element-4943" />
              </CardContent>
            </Card>}

          {/* ANGGOTA ORGANISASI - Required Documents */}
          {membershipType === 'organisasi' && <Card data-id="element-4944">
              <CardHeader data-id="element-4945">
                <CardTitle className="text-base sm:text-lg" data-id="element-4946">
                  Dokumen Organisasi
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 sm:space-y-6" data-id="element-4947">
                <DropZone label="Akta Pendirian atau SK Kemenkumham" type="aktaOrSK" maxSize={10 * 1024 * 1024} acceptedFormats={['application/pdf', 'image/jpeg', 'image/png']} required helperText="Akta Pendirian Organisasi atau SK dari Kemenkumham" data-id="element-4948" />
                <DropZone label="Profil Organisasi (Opsional)" type="organizationProfile" maxSize={10 * 1024 * 1024} acceptedFormats={['application/pdf', 'image/jpeg', 'image/png']} helperText="Company profile atau brosur organisasi" data-id="element-4949" />
              </CardContent>
            </Card>}

          {/* ANGGOTA BADAN USAHA - Required Documents */}
          {membershipType === 'badan_usaha' && <Card data-id="element-4950">
              <CardHeader data-id="element-4951">
                <CardTitle className="text-base sm:text-lg" data-id="element-4952">
                  Dokumen Perusahaan
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 sm:space-y-6" data-id="element-4953">
                <DropZone label="NIB atau Akta Pendirian" type="nibOrAkta" maxSize={10 * 1024 * 1024} acceptedFormats={['application/pdf', 'image/jpeg', 'image/png']} required helperText="Nomor Induk Berusaha (NIB) atau Akta Pendirian Perusahaan" data-id="element-4954" />
                <DropZone label="Dokumen NPWP" type="npwpDocument" maxSize={10 * 1024 * 1024} acceptedFormats={['application/pdf', 'image/jpeg', 'image/png']} required helperText="Kartu NPWP Perusahaan yang masih berlaku" data-id="element-4955" />
              </CardContent>
            </Card>}

          {/* MITRA ASOSIASI - Required Documents */}
          {membershipType === 'mitra' && <Card data-id="element-4956">
              <CardHeader data-id="element-4957">
                <CardTitle className="text-base sm:text-lg" data-id="element-4958">
                  Dokumen Kemitraan
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 sm:space-y-6" data-id="element-4959">
                <DropZone label="Dokumen MoU/Proposal Kemitraan" type="moaDocument" maxSize={10 * 1024 * 1024} acceptedFormats={['application/pdf', 'image/jpeg', 'image/png']} required helperText="Draft MoU atau proposal kemitraan dengan ATIOS" data-id="element-4960" />
                <DropZone label="Profil Organisasi (Opsional)" type="partnershipProposal" maxSize={10 * 1024 * 1024} acceptedFormats={['application/pdf', 'image/jpeg', 'image/png']} helperText="Company profile atau portofolio organisasi" data-id="element-4961" />
              </CardContent>
            </Card>}

          {/* Important Notes */}
          <Card className="bg-cyan-50 border-cyan-200" data-id="element-4962">
            <CardContent className="p-4 sm:p-6" data-id="element-4963">
              <h3 className="text-sm sm:text-base font-semibold text-neutral-900 mb-3" data-id="element-4964">
                Catatan Penting:
              </h3>
              <ul className="space-y-2 text-xs sm:text-sm text-neutral-700" data-id="element-4965">
                <li className="flex items-start gap-2" data-id="element-4966">
                  <span className="text-cyan-600 mt-0.5" data-id="element-4967">•</span>
                  <span data-id="element-4968">Semua dokumen harus jelas dan dapat dibaca</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-4969">
                  <span className="text-cyan-600 mt-0.5" data-id="element-4970">•</span>
                  <span data-id="element-4971">Format file yang diterima: PDF, JPG, PNG</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-4972">
                  <span className="text-cyan-600 mt-0.5" data-id="element-4973">•</span>
                  <span data-id="element-4974">Ukuran maksimal per file: 10MB</span>
                </li>
                {membershipType === 'badan_usaha' && <li className="flex items-start gap-2" data-id="element-4975">
                    <span className="text-error mt-0.5" data-id="element-4976">•</span>
                    <span className="font-medium" data-id="element-4977">
                      NIB/Akta Pendirian dan NPWP wajib diunggah untuk Badan
                      Usaha
                    </span>
                  </li>}
                {membershipType === 'organisasi' && <li className="flex items-start gap-2" data-id="element-4978">
                    <span className="text-error mt-0.5" data-id="element-4979">•</span>
                    <span className="font-medium" data-id="element-4980">
                      Akta Pendirian atau SK Kemenkumham wajib diunggah untuk
                      Organisasi
                    </span>
                  </li>}
                {membershipType === 'mitra' && <li className="flex items-start gap-2" data-id="element-4981">
                    <span className="text-error mt-0.5" data-id="element-4982">•</span>
                    <span className="font-medium" data-id="element-4983">
                      Dokumen MoU/Proposal Kemitraan wajib diunggah untuk Mitra
                    </span>
                  </li>}
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 sm:gap-4 mt-6 sm:mt-8" data-id="element-4984">
          <Button type="button" variant="secondary" onClick={() => navigate('/register/step2')} className="flex-1" size="lg" data-id="element-4985">
            <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 mr-2" data-id="element-4986" />
            Kembali
          </Button>
          <Button onClick={handleSubmit} className="flex-1 bg-cyan-500 hover:bg-cyan-600" size="lg" data-id="element-4987">
            Lanjutkan
            <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" data-id="element-4988" />
          </Button>
        </div>
      </div>
    </div>;
}